<template>
    <div
        ref="stickyNavWrapper"
        class="relative w-full bg-white hidden lg:block min-h-[50px]"
        :style="stickyNavWrapperStyle"
        @mouseleave="closeFlyout">
        <div
            ref="stickyNav"
            :class="navState.isFixed ? 'fixed top-0' : 'relative'"
            class="border-b w-full bg-white"
        >
            <nav
                id="mainnav"
                itemscope="itemscope"
                itemtype="https://schema.org/SiteNavigationElement"
                class="main-navigation-wrapper"
                role="navigation"
            >
                <div class="container mx-auto flex justify-between items-center">
                    <ul class="flex gap-8 py-px !max-w-full" role="menubar">
                        <li v-for="(item, index) in mainMenu" :key="index" role="none">
                            <template v-if="item.url">
                                <MiscLink
                                    :id="'menu-item-' + index"
                                    :to="item.externalLink ? item.externalLink : checkUrl(item.url, item.id)"
                                    class="text-lg font-semibold leading-6 block py-3 px-4 hover:bg-secondary hover:text-secondary-content"
                                    :class="{
                                        'bg-secondary text-secondary-content': isActive(item.url)
                                    }"
                                    role="menuitem"
                                    tabindex="0"
                                    :target="item.linkNewTab ? '_blank' : null"
                                    @mouseenter="openFlyoutTimeout(index)"
                                    @keydown.enter.prevent="openFlyout(index)"
                                    @keydown.down.prevent="openFlyout(index)"
                                >
                                    {{ item.name }}
                                </MiscLink>
                            </template>
                            <template v-else>
                                <button
                                    ref="mainButtons"
                                    class="text-lg font-semibold leading-6 block py-3 px-4 hover:bg-secondary hover:text-secondary-content"
                                    :class="{
                                        'bg-secondary text-secondary-content': isActive(item.url)
                                    }"
                                    :aria-haspopup="true"
                                    :aria-expanded="activeMainIndex === index"
                                    :aria-label="item.name"
                                    role="menuitem"
                                    tabindex="0"
                                    @mouseenter="openFlyout(index)"
                                    @keydown="handleMainKeydown($event, index)"
                                >
                                    {{ item.name }}
                                </button>
                            </template>
                        </li>
                    </ul>

                    <nav v-if="data?.saleTitle?.value">
                        <MiscLink
                            v-if="data?.saleTitle?.value"
                            :to="data?.saleUrl?.value"
                            :target="data?.saleMenu1NewTab?.value ? '_blank' : null"
                            class="text-lg font-semibold leading-6 block py-3 px-4 hover:bg-secondary hover:text-secondary-content"
                        >
                            {{ data?.saleTitle?.value }}
                        </MiscLink>
                    </nav>
                </div>
            </nav>

            <div
                v-if="activeMainIndex !== null"
                class="absolute left-0 w-full border-b border-t bg-white main-navigation-flyout z-50"
                role="menu"
                :aria-labelledby="'menu-item-' + activeMainIndex"
                @keydown.esc="closeFlyout"
            >
                <div class="container mx-auto">
                    <div class="grid grid-cols-12 py-8">
                        <div class="col-span-3">
                            <ul class="flex flex-col gap-2">
                                <li
                                    v-for="(sub, subIndex) in mainMenu[activeMainIndex].children"
                                    :key="subIndex"
                                    role="none"
                                    @mouseenter="activeSubIndex = subIndex"
                                    @keydown="handleSubKeydown($event, subIndex)"
                                >
                                    <template v-if="sub.url">
                                        <MiscLink
                                            :to="sub.externalLink ? sub.externalLink : checkUrl(sub.url, sub.id)"
                                            class="flex items-center gap-3 w-full text-link text-xl leading-8 min-h-10 py-2 px-4 hover:bg-secondary hover:text-secondary-content"
                                            :class="{
                                                'bg-secondary text-secondary-content': isActive(sub.url) || activeSubIndex === subIndex
                                            }"
                                            role="menuitem"
                                            tabindex="0"
                                            :target="sub.linkNewTab ? '_blank' : null"
                                            @keydown.enter.prevent="openSubItems(subIndex)"
                                            @keydown.down.prevent="openSubItems(subIndex)"
                                        >
                                            <MiscSvgLoader :key="sub.id" :icon="sub?.navIcon" class="h-8 w-8"/>
                                            {{ sub.name }}
                                        </MiscLink>
                                    </template>
                                    <template v-else>
                                        <button
                                            ref="submenuButtons"
                                            class="flex items-center gap-3 w-full text-link text-xl leading-8 min-h-10 py-2 px-4 hover:bg-secondary hover:text-secondary-content"
                                            role="menuitem"
                                            aria-haspopup="true"
                                            :aria-expanded="activeSubIndex === subIndex"
                                            tabindex="0"
                                        >
                                            <MiscSvgLoader :key="sub.id" :icon="sub?.navIcon" class="h-8 w-8"/>
                                            {{ sub.name }}
                                        </button>
                                    </template>
                                </li>
                            </ul>
                        </div>

                        <div v-if="activeSubIndex !== null" class="col-span-3">
                            <ul class="border-l-4 border-secondary h-full flex flex-col gap-2" role="menu">
                                <li v-for="(subSub, subSubIndex) in mainMenu[activeMainIndex].children[activeSubIndex].children" :key="subSubIndex" role="none">
                                    <template v-if="subSub.url">
                                        <MiscLink
                                            :to="subSub.externalLink ? subSub.externalLink : checkUrl(subSub.url, subSub.id)"
                                            class="flex justify-between items-center gap-3 w-full text-link text-xl leading-8 min-h-10 py-2 px-4 hover:bg-secondary hover:text-secondary-content"
                                            :class="{
                                                'bg-secondary text-secondary-content': isActive(subSub.url)
                                            }"
                                            role="menuitem"
                                            :target="subSub.linkNewTab ? '_blank' : null"
                                            tabindex="0"
                                        >
                                            <MiscSvgLoader :key="subSub.id" :icon="subSub?.navIcon" class="h-8 w-8"/>
                                            {{ subSub.name }}
                                        </MiscLink>
                                    </template>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {ref, nextTick} from 'vue';
import type {HblNavigation} from "@/utils/types";
import {useHead, useNuxtApp} from "#imports";
import { useRoute } from 'vue-router'

const props = defineProps<{
    navigation: HblNavigation | null
    data?: never | null
}>()


const loading = ref(null)
const nuxtApp = useNuxtApp()

const mainMenu = ref<object | null>(props.navigation);
const stickyNav = ref<HTMLElement | null>(null)
const stickyNavWrapper = ref<HTMLElement | null>(null)
const stickyNavWrapperStyle = ref<Partial<CSSStyleDeclaration>>({})

const activeMainIndex = ref<number | null>(null)
const activeSubIndex = ref<number | null>(0)
const mainButtons = ref<HTMLElement[]>([])
const submenuButtons = ref<HTMLElement[]>([])
const route = useRoute()

let flyoutTimeout: ReturnType<typeof setTimeout>

function openFlyoutTimeout (index: number) {
    clearTimeout(flyoutTimeout)

    flyoutTimeout = setTimeout(() => {
        openFlyout(index)
    }, 150)
}

const openFlyout = (index: number) => {
    clearTimeout(flyoutTimeout)

    if (activeMainIndex.value !== index) {
        activeMainIndex.value = index;

        const children = mainMenu?.value?.[index]?.children ?? [];
        const matchedSubIndex = children.findIndex(sub => isActive(sub.url));
        activeSubIndex.value = matchedSubIndex !== -1 ? matchedSubIndex : 0;
    }

    nextTick(() => {
        submenuButtons.value[activeSubIndex.value!]?.focus();
    });
};

const closeFlyout = () => {
    clearTimeout(flyoutTimeout)
    activeMainIndex.value = null;
    activeSubIndex.value = null;

    nextTick(() => {
        if (activeMainIndex.value !== null) {
            mainButtons.value[activeMainIndex.value]?.focus();
        }
    });
};

const handleMainKeydown = (event: KeyboardEvent, index: number) => {
    if (event.key === 'ArrowRight') {
        activeMainIndex.value = (index + 1) % mainMenu?.value?.length;
    } else if (event.key === 'ArrowLeft') {
        activeMainIndex.value = (index - 1 + mainMenu?.value?.length) % mainMenu?.value?.length;
    } else if (event.key === 'Enter' || event.key === ' ') {
        openFlyout(index);
    }
};

const handleSubKeydown = (event: KeyboardEvent, subIndex: number) => {
    if (event.key === 'ArrowDown') {
        activeSubIndex.value = (subIndex + 1) % mainMenu?.value[activeMainIndex.value!].children.length;
        nextTick(() => {
            submenuButtons.value[activeSubIndex.value!]?.focus();
        });
    } else if (event.key === 'ArrowUp') {
        activeSubIndex.value = (subIndex - 1 + mainMenu?.value[activeMainIndex.value!].children.length) % mainMenu?.value[activeMainIndex.value!].children.length;
        nextTick(() => {
            submenuButtons.value[activeSubIndex.value!]?.focus();
        });
    } else if (event.key === 'Escape') {
        closeFlyout();
    }
};

const openSubItems = (subIndex: number) => {
    if (activeSubIndex.value !== subIndex) {
        activeSubIndex.value = subIndex;

        nextTick(() => {
            submenuButtons.value[subIndex]?.focus();
        });
    }
};

function checkUrl(url: string, id: string) {
    return url || '/navigation/' + id
}

function isActive(itemUrl?: string): boolean {
    if (!itemUrl) return false
    return route.path === itemUrl || route.path.startsWith(itemUrl + '/')
}

const navState = reactive({
    isFixed: false,
    height: 0
});

// Pin the nav once its in-flow slot scrolls out of the viewport top.
// useIntersectionObserver auto-starts once the ref is set and cleans up on unmount.
useIntersectionObserver(
    stickyNavWrapper,
    ([entry]) => {
        navState.isFixed = !entry.isIntersecting
    },
    { threshold: 0 }
)

let heightObserver: ResizeObserver | null = null;

function reserveNavSpace() {
    // The nav is in normal flow by default (relative) and reserves its own
    // space — no padding, no measurement, no layout shift. Only while it is
    // pinned (isFixed → position: fixed, out of flow) do we reserve its height
    // on the wrapper so the content below doesn't jump.
    if (navState.isFixed && stickyNavWrapper.value && stickyNav.value) {
        stickyNavWrapperStyle.value = { paddingBottom: `${stickyNav.value.offsetHeight}px` }
    } else {
        stickyNavWrapperStyle.value = {}
    }
}

// Add/remove the reserved space exactly when the nav pins/unpins on scroll.
watch(() => navState.isFixed, reserveNavSpace)

onMounted(() => {
    nextTick(() => {
        reserveNavSpace()

        // While pinned, keep the reserved height in sync with the nav.
        // No-op in the default in-flow state.
        if (stickyNav.value && 'ResizeObserver' in window) {
            heightObserver = new ResizeObserver(() => reserveNavSpace())
            heightObserver.observe(stickyNav.value)
        }
    });

    window.addEventListener('resize', reserveNavSpace)
});

onUnmounted(() => {
    if (heightObserver) {
        heightObserver.disconnect()
    }

    window.removeEventListener('resize', reserveNavSpace)
});

const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": props.navigation?.map(item => (item.name))
}

nuxtApp.hook('page:finish', () => {
    // @ts-ignore
    loading.value?.finish()
    window.scrollTo(0, 0)
    closeFlyout()
})

useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(navigationSchema),
        }
    ]
})
</script>